import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, connectWS } from '../lib/api.js';
import { useTenantHref } from '../lib/tenantPath.jsx';

export default function Dashboard() {
  const tHref = useTenantHref();
  const [kpi, setKpi] = useState(null);
  const [patients, setPatients] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [deliveries, setDeliveries] = useState([]);

  async function load() {
    const [k, p, a, d] = await Promise.all([
      api.kpi(),
      api.patients({ status: 'pre-discharge' }),
      api.alerts({ status: 'open' }),
      api.deliveries({ status: 'pending' }),
    ]);
    setKpi(k);
    setPatients(p.patients);
    setAlerts(a.alerts);
    setDeliveries(d.deliveries);
  }

  useEffect(() => {
    load().catch(console.error);
    const ws = connectWS((m) => {
      if (['alert_updated','rx_updated','delivery_updated','integration_event','patient_updated'].includes(m.type))
        load().catch(console.error);
    });
    const interval = setInterval(load, 30_000);
    return () => { ws.close(); clearInterval(interval); };
  }, []);

  const today = kpi?.today || {};

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Dashboard</h1>
        <p className="text-sm text-slate-500">Operational snapshot, updates in real time</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi label="Rx Received Today"    value={today.rx_received_today  ?? '—'} />
        <Kpi label="Rx Filled Today"      value={today.rx_filled_today    ?? '—'} />
        <Kpi label="Rejected Today"       value={today.rx_rejected_today  ?? '—'} tone={today.rx_rejected_today > 0 ? 'warning' : 'neutral'} />
        <Kpi label="Avg Fill (min)"       value={today.avg_fill_min_today ?? '—'} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="card p-4 lg:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold">Discharging Soon</h2>
            <Link to={tHref('/patients')} className="text-sm text-brand-600 hover:underline">All patients →</Link>
          </div>
          <table className="w-full text-sm">
            <thead className="text-left text-slate-500 text-xs uppercase">
              <tr><th>Patient</th><th>Room</th><th>Discharge</th><th>Rx</th><th>Status</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {patients.length === 0 && <tr><td colSpan={5} className="py-4 text-center text-slate-400">No patients discharging soon</td></tr>}
              {patients.map(p => (
                <tr key={p.id}>
                  <td className="py-2">
                    <Link to={tHref(`/patients/${p.id}`)} className="text-brand-600 hover:underline font-medium">
                      {p.first_name} {p.last_name}
                    </Link>
                    <div className="text-xs text-slate-500">{p.mrn}</div>
                  </td>
                  <td>{p.room_number || '—'}</td>
                  <td>{relativeTime(p.discharge_planned_at)}</td>
                  <td>
                    <span className="badge-success mr-1">{p.rx_ready}✓</span>
                    {p.rx_rejected > 0 && <span className="badge-critical mr-1">{p.rx_rejected}✗</span>}
                    <span className="badge-neutral">{p.rx_count} total</span>
                  </td>
                  <td>{p.m2b_eligible
                    ? <span className="badge-success">M2B</span>
                    : <span className="badge-neutral">Walk-out</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="card p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold">Open Alerts</h2>
            <Link to={tHref('/alerts')} className="text-sm text-brand-600 hover:underline">All →</Link>
          </div>
          <ul className="space-y-2">
            {alerts.length === 0 && <li className="text-sm text-slate-400">No open alerts</li>}
            {alerts.slice(0,6).map(a => (
              <li key={a.id} className="border-l-4 pl-3 py-1 text-sm"
                  style={{ borderColor: a.severity === 'critical' ? '#dc2626' : a.severity === 'warning' ? '#d97706' : '#2563eb' }}>
                <div className="font-medium">{a.title}</div>
                <div className="text-xs text-slate-500">{a.message}</div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="card p-4">
        <h2 className="font-semibold mb-3">Pending Deliveries</h2>
        <ul className="divide-y divide-slate-100">
          {deliveries.length === 0 && <li className="text-sm text-slate-400 py-2">No pending deliveries</li>}
          {deliveries.map(d => (
            <li key={d.id} className="py-2 flex items-center justify-between text-sm">
              <div>
                <div className="font-medium">{d.first_name} {d.last_name} — {d.delivery_location}</div>
                <div className="text-xs text-slate-500">{d.prescriptions?.length || 0} prescriptions · scheduled {relativeTime(d.scheduled_at)}</div>
              </div>
              <span className="badge-warning">{d.status}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function Kpi({ label, value, tone = 'neutral' }) {
  const toneClasses = { neutral: 'text-slate-900', warning: 'text-amber-600', critical: 'text-red-600', success: 'text-emerald-600' };
  return (
    <div className="card p-4">
      <div className="text-xs uppercase text-slate-500 font-medium">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${toneClasses[tone]}`}>{value}</div>
    </div>
  );
}

function relativeTime(iso) {
  if (!iso) return '—';
  const ms = new Date(iso).getTime() - Date.now();
  const abs = Math.abs(ms);
  const mins = Math.round(abs / 60000);
  if (mins < 60) return `${mins}m ${ms < 0 ? 'ago' : 'from now'}`;
  const hrs = Math.round(mins / 60);
  return `${hrs}h ${ms < 0 ? 'ago' : 'from now'}`;
}
